import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, Users, MapPin, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const CircuitCard = ({ circuit, featured = false }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();

  // Get language prefix from current URL
  const getLocalizedPath = (path) => {
    const langMatch = location.pathname.match(/^\/(fr|en|ar|nl|es)/);
    const currentLang = langMatch ? langMatch[1] : lang || 'fr';
    return `/${currentLang}${path}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-800";
      case "moderate":
        return "bg-yellow-100 text-yellow-800";
      case "challenging":
        return "bg-red-100 text-red-800";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl bg-card shadow-md hover:shadow-2xl transition-all duration-500 ${
        featured ? "aspect-[3/4]" : "aspect-[4/3]"
      }`}
      data-testid={`circuit-card-${circuit.id}`}
    >
      {/* Image */}
      <div className="absolute inset-0">
        <img
          src={circuit.image_url}
          alt={circuit.title[lang] || circuit.title.fr}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      </div>

      {/* Badge Type */}
      <div className="absolute top-4 left-4 z-10">
        <Badge
          className={`${
            circuit.type === "4x4"
              ? "bg-accent text-white"
              : "bg-primary text-white"
          } px-3 py-1`}
        >
          {circuit.type === "4x4" ? "4x4" : "Trekking"}
        </Badge>
      </div>

      {/* Difficulty Badge */}
      <div className="absolute top-4 right-4 z-10">
        <Badge className={getDifficultyColor(circuit.difficulty)}>
          {t(`circuits.difficulty.${circuit.difficulty}`)}
        </Badge>
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
        <h3 className="font-serif text-xl md:text-2xl font-semibold text-white mb-2 line-clamp-2">
          {circuit.title[lang] || circuit.title.fr}
        </h3>

        <p className="text-white/80 text-sm mb-4 line-clamp-2">
          {circuit.description[lang] || circuit.description.fr}
        </p>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-4 text-white/90 text-sm">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>
              {circuit.duration_days} {t("circuits.duration")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            <span>Max {circuit.max_group_size}</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            <span>{circuit.departure_city}</span>
          </div>
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-white/70 text-sm">{t("circuits.from")}</span>
            <p className="text-white text-2xl font-bold">
              €{circuit.price}
              <span className="text-sm font-normal text-white/70 ml-1">
                /{t("circuits.perPerson")}
              </span>
            </p>
          </div>
          <Link to={getLocalizedPath(`/circuit/${circuit.id}`)}>
            <Button
              variant="secondary"
              className="rounded-full group-hover:bg-primary group-hover:text-white transition-colors"
              data-testid={`view-circuit-${circuit.id}`}
            >
              {t("circuits.viewDetails")}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CircuitCard;
